<template>
  <div class="w-[95%] mx-auto  my-[20px]">

    <div class="flex justify-between">
      <img
        :alt="data.distributorName"
        class="mb-2"
        :src="data.distributorLogo"
        v-if="data.distributorLogo"
        style="height: 80px; width: 200px"
      >
      <h1
        class="font-bold text-2xl"
        :class="data.paid ? 'text-green-500' : 'text-red-600'"
      >
        {{ data.paid ? 'PAID' : 'UNPAID' }}
      </h1>
    </div>
    <h1 class="font-bold text-2xl">Invoice {{ data.number }}</h1>
    <hr class="mb-4 mt-2">
    <div class="sm:flex sm:justify-between">
      <div>
        <p class="font-bold">Invoice To</p>
        <p class="text-sm">{{ data.businessName }}</p>
        <p class="text-sm">{{ data.ownerTelephone }}</p>
        <p class="text-sm">{{ data.ownerFullNames }}</p>
        <p class="text-sm">{{ data.businessAddress }}</p>
      </div>
      <div class="sm:text-right mt-4 sm:mt-0">
        <p class="font-bold">Pay To</p>
        <p class="text-sm">{{ data.distributorName }}</p>
        <p class="text-sm">{{ data.distributorTelephone }}</p>
        <p class="text-sm">{{ data.distributorEmail }}</p>
      </div>
    </div>
    <hr class="mb-4 mt-2">
    <div class="sm:flex sm:justify-between">
      <div>
        <h6 class="font-bold">Invoice Date</h6>
        <p class="text-sm">{{ createdDate }}</p>
      </div>
      <div>
        <h6 class="font-bold">Delivery Method</h6>
        <p class="text-sm">{{ data.deliveryMethod }}</p>
      </div>
      <div class="sm:text-right sm:mt-0" v-if="data?.paymentMethod?.method">
        <h6 class="font-bold">Payment Method</h6>
        <p class="text-sm">{{ data.paymentMethod.method }}</p>
      </div>
    </div>
    <hr class="my-4">
    <div class="relative overflow-x-auto">
      <table class="w-full text-sm text-left rtl:text-right border border-gray-200" aria-describedby="items">
        <thead class="text-xs text-gray-700 uppercase bg-gray-100">
          <tr>
            <th scope="col" class="px-6 py-3">
              Description
            </th>
            <th scope="col" class="px-6 py-3">
              QTY
            </th>
            <th scope="col" class="px-6 py-3">
              Price
            </th>
            <th scope="col" class="px-6 py-3">
              Total Amount
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="bg-white border-b"
            :key="index"
            v-for="(item, index) in items.values"
          >
            <th scope="row" class="px-6 py-4 font-medium  whitespace-nowrap">
              {{ item.name }}
            </th>
            <td class="px-6 py-4">
              {{ item.quantity }}
            </td>
            <td class="px-6 py-4">
              {{ currency }}{{ formattedCost(item.price) }}
            </td>
            <td class="px-6 py-4">
              {{ currency }}{{ formattedCost(item.totalAmount) }}
            </td>
          </tr>
          <slot>
            <tr>
              <td />
              <td />
              <td class="px-6 py-1 text-gray-500 font-semibold">Sub Total</td>
              <td class="px-6 py-1">{{ currency }}{{ formattedCost(subTotal) }}</td>
            </tr>
            <tr v-if="false">
              <td />
              <td />
              <td class="px-6 py-1 text-gray-500 font-semibold">Discount</td>
              <td class="px-6 py-1">{{ currency }}{{ discount }}</td>
            </tr>
            <tr v-if="false">
              <td />
              <td />
              <td class="px-6 py-1 text-gray-500 font-semibold">Tax (0%)</td>
              <td class="px-6 py-1">R0</td>
            </tr>
            <tr>
              <td />
              <td />
              <td class="px-6 py-1 text-gray-500 font-semibold">Total</td>
              <td class="px-6 py-1 font-bold">{{ currency }}{{ formattedCost(total) }}</td>
            </tr>
            <tr v-if="totalPayments > 0">
              <td />
              <td />
              <td class="px-6 py-1 text-gray-500 font-bold">Payment Made</td>
              <td class="px-6 py-1 font-bold text-green-400">+{{ currency }}{{ formattedCost(totalPayments) }}</td>
            </tr>
            <tr class="bg-gray-200">
              <td />
              <td />
              <td class="px-6 py-1 text-gray-500 font-semibold">Balance Due</td>
              <td class="px-6 py-1 font-bold">{{ currency }}{{ formattedCost(balance) }}</td>
            </tr>
          </slot>
        </tbody>
      </table>
    </div>
    <div
      class="mt-4"
      v-if="data?.paymentMethod?.method"
    >
      <p class="font-bold">Payment Instructions</p>
      <p class="text-sm" style="white-space: pre-wrap;">{{ data.paymentMethod.instructions }}</p>
      <hr class="my-4">
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});
const createdDate = computed(() => {
  if (!props.data.createdDate) return null;
  return format(new Date(props.data.createdDate), 'd MMM yyyy, HH:mm');
});

const currency = computed(() => props.data?.currency);
const formattedCost = (cost) => new Intl.NumberFormat().format(cost);

// const withTax = (value) => {
//   if (!props.data?.tax) return value;
//   return (value*props.data?.tax/100)
// };
const items = computed(() => {
  if (!props.data.items) return [];
  const values = props
    .data
    .items
    .map((item) => ({
      name: item.name,
      quantity: item.quantity,
      price: parseFloat(item.price),
      totalAmount: parseFloat(item.quantity * item.price),
    }));
  return {
    values,
    total: values
      .reduce((accumulator, curValue) => accumulator + curValue.totalAmount, 0),
  };
});
const tax = computed(() => {
  if (!props.data.tax) return 0;
  return 0;
});
const discount = computed(() => 0);
const subTotal = computed(() => items.value.total - tax.value);
const total = computed(() => subTotal.value + tax.value + discount.value);

const totalPayments = computed(() => {
  const { payments = [] } = props.data;
  const paymentMade = payments
    .reduce((accumulator, curValue) => accumulator + parseFloat(curValue.amount), 0);
  if (paymentMade > total.value) {
    return total.value;
  }
  return paymentMade;
});

const balance = computed(() => {
  const blnc = total.value - totalPayments.value;
  return blnc <= 0 ? 0 : blnc;
});

</script>
